import {
  DocumentTextIcon,
  PhotoIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { JWT_HOST_API } from 'configs/auth.config';
import { formatDisplayDate } from 'utils/dateUtils';
import { downloadLeadDocument } from 'services/leadsService';
import { downloadJobDocument } from 'services/jobsService';
import { downloadDocument } from 'services/documentServices';
import { getAttachmentPreviewUrl } from 'services/documentUploadService';

export const getDocumentUrl = (filePath) => {
  if (!filePath) return '';
  if (typeof filePath === 'string' && (filePath.startsWith('http://') || filePath.startsWith('https://'))) {
    return filePath;
  }

  const normalized = filePath.replace(/\\/g, '/');
  let relativePath = normalized;
  const index = normalized.toLowerCase().indexOf('uploads/');
  if (index !== -1) {
    relativePath = normalized.substring(index);
  }

  return `${JWT_HOST_API}${relativePath}`;
};

// File type icon mapping
const FILE_TYPE_ICONS = {
  pdf: DocumentTextIcon,
  doc: DocumentTextIcon,
  docx: DocumentTextIcon,
  xls: TableCellsIcon,
  xlsx: TableCellsIcon,
  csv: TableCellsIcon,
  jpg: PhotoIcon,
  jpeg: PhotoIcon,
  png: PhotoIcon,
  gif: PhotoIcon,
  svg: PhotoIcon,
  webp: PhotoIcon,
  default: DocumentTextIcon,
};

// File type color mapping
const FILE_TYPE_COLORS = {
  pdf: "text-primary-600 dark:text-primary-400",
  doc: "text-primary-600 dark:text-primary-400",
  docx: "text-primary-600 dark:text-primary-400",
  xls: "text-primary-600 dark:text-primary-400",
  xlsx: "text-primary-600 dark:text-primary-400",
  csv: "text-primary-600 dark:text-primary-400",
  jpg: "text-primary-600 dark:text-primary-400",
  jpeg: "text-primary-600 dark:text-primary-400",
  png: "text-primary-600 dark:text-primary-400",
  gif: "text-primary-600 dark:text-primary-400",
  svg: "text-primary-600 dark:text-primary-400",
  webp: "text-primary-600 dark:text-primary-400",
  default: "text-primary-600 dark:text-primary-400",
};

export const getFileIcon = (fileType) => {
  const Icon = FILE_TYPE_ICONS[fileType] || FILE_TYPE_ICONS.default;
  const colorClass = FILE_TYPE_COLORS[fileType] || FILE_TYPE_COLORS.default;
  return { Icon, colorClass };
};

export const getFileTypeLabel = (fileType) => {
  if (!fileType) return "File";
  const ext = String(fileType).toLowerCase().replace(".", "");
  const labels = {
    pdf: "PDF Document",
    doc: "Word Document",
    docx: "Word Document",
    xls: "Excel Sheet",
    xlsx: "Excel Sheet",
    csv: "CSV File",
    jpg: "JPEG Image",
    jpeg: "JPEG Image",
    png: "PNG Image",
    gif: "GIF Image",
    svg: "SVG Image",
    webp: "WebP Image",
    zip: "ZIP Archive",
    rar: "RAR Archive",
    txt: "Text Document",
  };
  return labels[ext] || `${ext.toUpperCase()} File`;
};

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  const formatted = formatDisplayDate(dateString);
  if (!formatted) return "-";
  return String(formatted).replace(/\s*\(Today\)/gi, "").replace(/\s*\(Yesterday\)/gi, "").trim();
};

export const getInitials = (name) => {
  if (!name || typeof name !== 'string') {
    return 'U';
  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 0) return 'U';

  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const VERIFICATION_STATUS_LABELS = {
  0: "Pending",
  1: "Verified",
  2: "Rejected",
};

const VERIFICATION_STATUS_COLORS = {
  0: "warning",
  1: "success",
  2: "error",
};

export const getVerificationStatusInfo = (status) => {
  const numericStatus = typeof status === "number" ? status : parseInt(status ?? 0, 10) || 0;
  return {
    label: VERIFICATION_STATUS_LABELS[numericStatus] || VERIFICATION_STATUS_LABELS[0],
    color: VERIFICATION_STATUS_COLORS[numericStatus] || VERIFICATION_STATUS_COLORS[0],
    value: numericStatus,
  };
};

const STATUS_METADATA_LABELS = {
  1: {
    title: "Verified By",
    dateLabel: "Verified On",
    remarkLabel: "Verification Remark",
  },
  2: {
    title: "Rejected By",
    dateLabel: "Rejected On",
    remarkLabel: "Rejection Remark",
  },
  0: {
    title: "Assigned To",
    dateLabel: "Assigned On",
    remarkLabel: "Notes",
  },
};

export const getVerificationMetadataLabels = (status) => {
  const info = getVerificationStatusInfo(status);
  return {
    ...info,
    meta: STATUS_METADATA_LABELS[info.value] || STATUS_METADATA_LABELS[0],
  };
};

const isGuidStr = (val) =>
  typeof val === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val.trim());

export const getUploaderName = (item) => {
  if (!item) return "System";
  const candidates = [
    item.UploadedByName,
    item.uploadedByName,
    item.UploadedBy,
    item.uploadedBy,
    item.CreatedByName,
    item.createdByName,
    item.CreatedBy,
    item.createdBy,
  ].filter((val) => val && typeof val === "string" && val.trim() !== "");

  const nonGuid = candidates.find((val) => !isGuidStr(val));
  return nonGuid || candidates[0] || "System";
};

export const getModifierName = (item) => {
  if (!item) return "-";
  const candidates = [
    item.ModifiedByName,
    item.modifiedByName,
    item.ModifiedBy,
    item.modifiedBy,
    item.UpdatedByName,
    item.updatedByName,
    item.UpdatedBy,
    item.updatedBy,
  ].filter((val) => val && typeof val === "string" && val.trim() !== "");

  const nonGuid = candidates.find((val) => !isGuidStr(val));
  if (nonGuid) return nonGuid;
  if (candidates[0]) return candidates[0];

  return "-";
};

export const downloadDocumentWithToast = async (doc, options = {}) => {
  if (!doc) {
    toast.error("Document reference unavailable");
    return { success: false };
  }

  const docId = doc.id || doc.DocumentId || doc.CompanyDocumentId;
  const docName = doc.name || doc.FileName || doc.fileName || doc.OriginalFileName || "document";
  const filePathKey =
    doc.filePath || doc.FilePath || doc.url || doc.Url || doc.FileKey || doc.fileKey || doc.FileUrl || doc.StoredFileName || "";
  const lowerPath = typeof filePathKey === "string" ? filePathKey.toLowerCase() : "";

  const isJobDoc =
    options.entityType === 2 ||
    doc.isJobDocument ||
    lowerPath.startsWith("job-documents/");

  const toastId = toast.loading(`Preparing download for "${docName}"...`);

  try {
    let result = null;

    if (doc instanceof File || doc instanceof Blob) {
      const blobUrl = window.URL.createObjectURL(doc);
      const link = window.document.createElement("a");
      link.href = blobUrl;
      link.download = docName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
      result = { success: true, message: "Download started" };
    } else if (doc.rawFile && (doc.rawFile instanceof File || doc.rawFile instanceof Blob)) {
      const blobUrl = window.URL.createObjectURL(doc.rawFile);
      const link = window.document.createElement("a");
      link.href = blobUrl;
      link.download = docName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
      result = { success: true, message: "Download started" };
    } else if (doc.previewUrl && doc.previewUrl.startsWith("blob:")) {
      const link = window.document.createElement("a");
      link.href = doc.previewUrl;
      link.download = docName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      result = { success: true, message: "Download started" };
    } else if ((options.isCompanyDoc || doc.isCompanyDoc || doc.CompanyDocumentId || lowerPath.includes("company-documents")) && (doc.CompanyDocumentId || docId)) {
      const compDocId = doc.CompanyDocumentId || docId;
      result = await downloadDocument(5, compDocId, docName);
    } else if (isJobDoc && docId && !String(docId).startsWith("doc-") && !String(docId).startsWith("dynamic-") && !doc.isAdditionalDocument) {
      result = await downloadJobDocument(docId, docName);
    } else if (docId && !String(docId).startsWith("doc-") && !String(docId).startsWith("dynamic-") && !doc.isAdditionalDocument && !options.isCompanyDoc && !doc.isCompanyDoc) {
      result = await downloadLeadDocument(docId, docName);
    } else if (filePathKey) {
      let url = "";
      if (typeof filePathKey === "string" && (filePathKey.startsWith("http://") || filePathKey.startsWith("https://"))) {
        url = filePathKey;
      } else {
        try {
          const previewRes = await getAttachmentPreviewUrl(filePathKey);
          if (previewRes?.success && previewRes?.url) {
            url = previewRes.url;
          }
        } catch (e) {
          console.warn("Failed to get preview URL for download", e);
        }
        if (!url) {
          url = getDocumentUrl(filePathKey);
        }
      }

      if (url) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = window.document.createElement("a");
            link.href = blobUrl;
            link.download = docName;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
            result = { success: true, message: "Download started" };
          } else {
            throw new Error(`HTTP ${response.status}`);
          }
        } catch (fetchErr) {
          console.warn("Direct fetch failed for download, executing anchor fallback:", fetchErr);
          const link = window.document.createElement("a");
          link.href = url;
          link.download = docName;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
          result = { success: true, message: "Download started" };
        }
      } else {
        result = { success: false, error: "Document download URL unavailable" };
      }
    } else {
      result = { success: false, error: "Invalid document ID or document not uploaded yet" };
    }

    if (result?.success) {
      toast.success(result.message || "Download started", { id: toastId });
      return { success: true };
    } else {
      toast.error(result?.error || result?.message || "Failed to download document", { id: toastId });
      return { success: false };
    }
  } catch (error) {
    console.error("Error downloading file:", error);
    toast.error(error.message || "An error occurred during file download", { id: toastId });
    return { success: false };
  }
};


